import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Users, Package, Calendar, HelpCircle } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const GlobalSearch = ({ teamMembers, assets, trips, onNavigate, onHelpClick }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  // Help suggestions
  const helpSuggestions = [
    { text: 'How to add a teamMember', action: 'help-add-teamMember' },
    { text: 'How to create a trip', action: 'help-create-trip' },
    { text: 'How to assign a asset', action: 'help-assign-asset' },
    { text: 'How to use auto-scheduler', action: 'help-auto-schedule' },
    { text: 'How to bulk import trips', action: 'help-bulk-import' },
    { text: 'How to use payroll system', action: 'payroll' },
    { text: 'How to setup customer bookings', action: 'bookings' },
    { text: 'How to share teamMember booking links', action: 'teamMemberLinks' },
    { text: 'How to configure booking widget', action: 'bookings' },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = [];

    // Search teamMembers
    teamMembers?.forEach(teamMember => {
      if (
        teamMember.name?.toLowerCase().includes(query) ||
        teamMember.email?.toLowerCase().includes(query) ||
        teamMember.phone?.includes(query) ||
        teamMember.username?.toLowerCase().includes(query)
      ) {
        results.push({
          type: 'teamMember',
          id: teamMember.id,
          title: teamMember.name,
          subtitle: teamMember.email || teamMember.phone || teamMember.username,
          icon: Users,
          tab: 'teamMembers'
        });
      }
    });

    // Search assets
    assets?.forEach(asset => {
      if (
        asset.name?.toLowerCase().includes(query) ||
        asset.type?.toLowerCase().includes(query)
      ) {
        results.push({
          type: 'asset',
          id: asset.id,
          title: asset.name,
          subtitle: asset.type,
          icon: Package,
          tab: 'assets'
        });
      }
    });

    // Search trips
    trips?.forEach(trip => {
      const teamMember = teamMembers?.find(c => c.id === trip.teamMemberId);
      const asset = assets?.find(v => v.id === trip.assetId);
      const tripDate = new Date(trip.date).toLocaleDateString();
      
      if (
        trip.date?.includes(query) ||
        tripDate.toLowerCase().includes(query) ||
        teamMember?.name?.toLowerCase().includes(query) ||
        asset?.name?.toLowerCase().includes(query)
      ) {
        results.push({
          type: 'trip',
          id: trip.id,
          title: `Booking on ${tripDate}`,
          subtitle: `${teamMember?.name || 'Unassigned'} • ${asset?.name || 'No asset'}`,
          icon: Calendar,
          tab: 'trips'
        });
      }
    });

    // Add help suggestions if query matches
    helpSuggestions.forEach(help => {
      if (help.text.toLowerCase().includes(query)) {
        results.push({
          type: 'help',
          id: help.action,
          title: help.text,
          subtitle: 'Help & Tutorial',
          icon: HelpCircle,
          action: help.action
        });
      }
    });

    setSearchResults(results.slice(0, 10)); // Limit to 10 results
    setShowResults(results.length > 0);
  }, [searchQuery, teamMembers, assets, trips]);

  const handleResultClick = (result) => {
    if (result.type === 'help') {
      onHelpClick(result.action);
    } else {
      onNavigate(result.tab, result.id);
    }
    setSearchQuery('');
    setShowResults(false);
  };

  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Search team, assets, bookings, or ask for help..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => searchQuery && setShowResults(true)}
          className="pl-10 pr-10 py-2 w-full"
        />
        {searchQuery && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && (
        <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto z-50">
          {searchResults.length > 0 ? (
            <div className="py-2">
              {searchResults.map((result, index) => {
                const Icon = result.icon;
                return (
                  <button
                    key={`${result.type}-${result.id}-${index}`}
                    onClick={() => handleResultClick(result)}
                    className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className={`p-2 rounded-lg ${
                      result.type === 'teamMember' ? 'bg-blue-100 text-blue-600' :
                      result.type === 'asset' ? 'bg-purple-100 text-purple-600' :
                      result.type === 'trip' ? 'bg-green-100 text-green-600' :
                      'bg-orange-100 text-orange-600'
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{result.title}</p>
                      <p className="text-sm text-gray-500 truncate">{result.subtitle}</p>
                    </div>
                    <span className="text-xs text-gray-400 capitalize mt-1">
                      {result.type}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-gray-500">
              <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No results found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
